import { Dispatch, SetStateAction } from "react";
import { FetchNextPageOptions, InfiniteData, InfiniteQueryObserverResult } from "react-query";
import {
  Control, FieldErrors, UseFormHandleSubmit, UseFormSetValue,
} from "react-hook-form";
import {
  IBusinessTrx, IGetBusinessTrxParams,
  IArea,
  ICompanyByPool,
  IDepartment,
  IGetTrxAreaResponse,
  IGetTrxCompanyResponse,
  IGetTrxDepartmentByCompanyResponse,
  IGetTrxGlListResponse,
  IGetTrxPoolResponse,
  IGetTrxTypeListResponse,
  IPoolsByArea,
} from "../../../services/businessTrxService/types";
import { IOptionItem, IOptionItemAutoComplete, MetadataI } from "../../../typings/common";
import { ColumnI } from "../../../components/Table/TableProps";

export type ModalType = "" | "DELETE" | "CONFIRMATION"
export type TrxType = "Debit" | "Kredit" | "Semua"
export enum CodeType {
  Pool = "P",
  Hotel = "H",
  HO = "O",
}

export type IBusinessTrxFormValues = {
  currentStep: number;
  detailTransaksi: {
    gl: string;
    deskripsi: string;
    deskripsiSap: string;
    tipeTransaksi: string;
    sap_shkzg: string;
    trx_code: string;
    code_type: string;
  };
  selectedAreaIds: string[];
  pilihSemuaPool: boolean;
  selectedPoolIds: string[];
  pilihSemuaPerusahaan: boolean;
  selectedCompanyIds: string[];
  selectedCompanyRelations: string[];
  pilihSemuaSegmen: boolean;
  selectedSegmentIds: string[];
  pilihSemuaDepartemen: boolean;
  selectedDepartmentIds: string[];
  poolsForValidation?: string;
  departmentsForValidation?: string;
  resPoolByAreaForValidation?: string;
  resCompanyByPoolForValidation?: string;
};

export interface IContextValue {
  params: IGetBusinessTrxParams;
  setParams: Dispatch<SetStateAction<IGetBusinessTrxParams>>;
  meta: MetadataI;
  setMeta: Dispatch<SetStateAction<MetadataI>>;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  observerRef: React.RefObject<HTMLTableSectionElement>
  hasNextPage?: boolean;
  handleDelete: () => void;
  businessTrxList: IBusinessTrx[];
  columns?: ColumnI[];
  action: string | null;
  tableRef: React.RefObject<HTMLTableElement>;
  modal: ModalType;
  setModal: React.Dispatch<React.SetStateAction<ModalType>>;
  selectedTrx: IBusinessTrx | null;
  setSelectedTrx: React.Dispatch<React.SetStateAction<IBusinessTrx | null>>;
  isEditMode: boolean;
  currentTrxFormValues: IBusinessTrxFormValues;
  control: Control<IBusinessTrxFormValues>;
  setValue: UseFormSetValue<IBusinessTrxFormValues>;
  handleSubmit: UseFormHandleSubmit<IBusinessTrxFormValues>;
  currentStep: { name: string; idx: number; };
  setCurrentStep: React.Dispatch<React.SetStateAction<{ name: string; idx: number; }>>;
  formId: string;
  onCancelFormClick: () => void;
  onSubmitFormClick: () => void;
  previousStep: () => void;
  nextStep: () => void;
  onFormSubmit: (data: IBusinessTrxFormValues) => void;
  onFormError: (formErrors: FieldErrors<IBusinessTrxFormValues>) => void;
  areaOptions: IOptionItem[];
  poolOptions: IOptionItem[];
  companyOptions: IOptionItem[];
  segmentOptions: IOptionItem[];
  departmentOptions: IOptionItem[];
  filterConfigs: {
    label: string;
    paramKey: string;
    options: IOptionItem[];
    disabled: boolean;
  }[];
  handleFilterSelect: (paramKey: string, selectedOptions: IOptionItem[]) => void;
}

export interface IGroupedPools {
  [area_name: string]: {
    pool_id: string;
    pool_name: string;
  }[];
}

export interface IGroupedCompanies {
  [area_name: string]: {
    [pool_name: string]: {
      pool_id: string;
      companies: { company_code: string, company_relation: string }[];
    };
  };
}

export interface IGroupedSegments {
  [area_name: string]: {
    [pool_name: string]: {
      pool_id: string;
      companies: {
        [company_code: string]: {
          segment_code: string;
          segment_name: string;
        }[];
      };
    };
  };
}

export type CompanyPoolData = {
  pool_id: string;
  companies: { company_code: string, company_relation: string }[];
};

export type CompanyPools = {
  [pool_name: string]: CompanyPoolData;
};

export type SegmentData = {
  segment_code: string;
  segment_name: string;
};

export type SegmentCompanies = {
  [company_code: string]: SegmentData[];
};

export type SegmentPoolData = {
  pool_id: string;
  companies: SegmentCompanies;
};

export type SegmentPools = {
  [pool_name: string]: SegmentPoolData;
};

export interface IFormBusinessTrxContext {
  rawTrxType?: IGetTrxTypeListResponse;
  rawTrxGl?: InfiniteData<IGetTrxGlListResponse>;
  hasNextGl?: boolean;
  fetchNextGl: (options?: FetchNextPageOptions) =>
    Promise<InfiniteQueryObserverResult<IGetTrxGlListResponse, unknown>>;
  rawArea?: IGetTrxAreaResponse;
  isFetchingArea: boolean;
  rawPools?: IGetTrxPoolResponse;
  isFetchingPool: boolean;
  rawCompanies?: IGetTrxCompanyResponse;
  isFetchingCompany: boolean;
  rawSegments?: IGetTrxCompanyResponse;
  isFetchingSegment: boolean;
  rawDepartments?: IGetTrxDepartmentByCompanyResponse;
  isFetchingDepartment: boolean;
  trxGlOptions: IOptionItemAutoComplete[];
  trxTypeOptions: IOptionItemAutoComplete[];
  glValue: string;
  selectedAreaIds: string[];
  selectedPoolIds: string[];
  selectedCompanyIds: string[];
  selectedCompanyRelations: string[];
  selectedSegmentIds: string[];
  selectedDepartmentIds: string[];
  paramsGl: {
    search: string;
    page_number: number;
  };
  setParamsGl: React.Dispatch<React.SetStateAction<{
    search: string;
    page_number: number;
  }>>;
  setDebouncedSearchGl: (search: string) => void;
  resArea: IArea[];
  resPoolByArea: IPoolsByArea[];
  resCompanyByPool: ICompanyByPool[];
  resSegmentByCompany: ICompanyByPool[];
  resDepartment: Pick<IDepartment, "department">[];
  pools: IGroupedPools;
  companies: IGroupedCompanies;
  segments: IGroupedSegments;
  allAreaIds: string[];
  allPoolIds: string[]
  allCompanyIds: string[];
  allCompanyRelations: string[];
  allSegmentIds: string[];
  allDepartmentIds: string[];
  isEditMode: boolean;
  detailTransactionValues: {
    gl: string;
    deskripsi: string;
    deskripsiSap: string;
    tipeTransaksi: string;
    sap_shkzg: string;
    trx_code: string;
    code_type: string;
  };
}

export type StepConfigType = {
  label: string;
  name: string;
  Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement> & {
    title?: string;
  }>;
}
